import { ApiError, captureRecapBoundary, getFeedbackDraft, getFeedbackPublication } from '@/lib/api';
import { isCurrentDraftContext, type DraftContext } from '@/features/coach-review/composerDrafts';
import type { PrivateCoachingFeedbackAcceptedResponse } from '@/lib/types';

export type SavedPrivateFeedbackRequest = Readonly<{
  owner: DraftContext;
  accepted: PrivateCoachingFeedbackAcceptedResponse;
}>;

/** A fresh read result, not an editing grant or an acknowledgment of a write. */
export type SavedPrivateFeedbackParent = Readonly<{
  feedbackId: string;
  feedbackVersion: number;
  publicationState: 'draft' | 'published';
}>;

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const validId = (value: unknown): value is string => typeof value === 'string' && uuid.test(value);
const validVersion = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 2147483647;

/** Explicit Open saved Feedback only. The caller owns cancellation on close,
 * replacement or unmount and must recheck its lifetime before opening a host.
 * No cache, workspace creation, write, receipt reconciliation or UI activation.
 */
export async function resolveSavedPrivateFeedback(
  input: SavedPrivateFeedbackRequest,
  signal: AbortSignal,
): Promise<SavedPrivateFeedbackParent> {
  const receipt = { ...input.accepted };
  if (receipt.schema_version !== 'coaching-private-feedback-accepted.v1'
      || receipt.artifact_kind !== 'feedback' || receipt.state !== 'private_draft_at_acceptance'
      || !validId(receipt.feedback_id) || !validId(receipt.revision_id) || !validVersion(receipt.version)) {
    throw new Error('invalid_private_feedback_receipt');
  }
  return resolveCurrentFeedbackParent(input.owner, receipt.feedback_id, signal);
}

/** Owning reads of one explicitly selected parent. Callers validate their own
 * entry evidence; neither historical receipts nor focus versions supply CAS. */
export async function resolveCurrentFeedbackParent(
  inputOwner: DraftContext, inputId: string, signal: AbortSignal, requireCreator = false,
): Promise<SavedPrivateFeedbackParent> {
  if (!validId(inputId)) throw new Error('invalid_saved_feedback_parent');
  const owner = Object.freeze({ ...inputOwner });
  const feedbackId = inputId.toLowerCase();
  const boundary = captureRecapBoundary({ ...owner, view: 'coach' });
  const assertCurrent = () => {
    if (signal.aborted || !boundary.current() || !isCurrentDraftContext(owner)) {
      throw new DOMException('Saved Feedback context changed', 'AbortError');
    }
  };
  const options = { scope: owner, signal };
  try {
    assertCurrent();
    let draft: Awaited<ReturnType<typeof getFeedbackDraft>> | undefined;
    let privateUnavailable = false;
    try {
      draft = await getFeedbackDraft(feedbackId, options);
    } catch (error) {
      assertCurrent();
      // Q04 intentionally hides a later-published parent. Only this exact
      // response permits a separate authorized Q05 read of that same identity.
      if (!(error instanceof ApiError) || error.status !== 404
          || !error.detail || typeof error.detail !== 'object'
          || !('code' in error.detail) || error.detail.code !== 'feedback_unavailable') throw error;
      privateUnavailable = true;
    }
    assertCurrent();
    if (!privateUnavailable) {
      if (!draft || draft.schema_version !== 'feedback-draft.v1' || draft.publication_state !== 'draft'
          || !validId(draft.feedback_id) || draft.feedback_id.toLowerCase() !== feedbackId
          || !validId(draft.revision_id) || !validVersion(draft.version)) {
        throw new Error('invalid_saved_feedback_parent');
      }
      return Object.freeze({ feedbackId, feedbackVersion: draft.version, publicationState: 'draft' });
    }
    const publication = await getFeedbackPublication(feedbackId, options);
    assertCurrent();
    if (!publication || publication.schema_version !== 'feedback-publication.v1'
        || !validId(publication.feedback_id) || publication.feedback_id.toLowerCase() !== feedbackId
        || !validId(publication.feedback_revision_id) || !validVersion(publication.feedback_version)) {
      throw new Error('invalid_saved_feedback_parent');
    }
    if (requireCreator && publication.can_edit_feedback !== true)
      throw new Error('feedback_creator_required');
    return Object.freeze({ feedbackId, feedbackVersion: publication.feedback_version, publicationState: 'published' });
  } catch (error) {
    assertCurrent();
    throw error;
  } finally {
    boundary.dispose();
  }
}
